import { Text, View } from "react-native";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import type { PlanAction } from "../plan.types";
import {
  TimelineActionRow,
  TimelineSubtaskCard,
} from "./TimelineTaskComponents";
import { StepperSection } from "./Stepper";
import { OrganicMiniIcon } from "./OrganicMiniIcon";
import {
  BiologyDeepCarousel,
  CoverageActionRow,
  OutcomeEstimateFunnelSection,
  type DeepBiologySlide,
} from "./EggFreezingBiologyDeepCarouselCard";

function buildEmbryoFreezingSlides(t: TranslationFunction): DeepBiologySlide[] {
  return [
    {
      id: "embryo-egg-supply",
      visual: "reserve",
      label: t("homepage.review_concept.bio_emb_supply_label"),
      title: t("homepage.review_concept.bio_emb_supply_title"),
      body: t("homepage.review_concept.bio_emb_supply_body"),
      calloutLabel: t("homepage.review_concept.bio_callout_why_matters"),
      callout: t("homepage.review_concept.bio_emb_supply_callout"),
      cta: {
        label: t("homepage.review_concept.bio_cta_test_ovarian"),
        screen: "AtHomeFertilityTesting",
      },
      detail: {
        title: t("homepage.review_concept.bio_emb_supply_detail_title"),
        sections: [
          {
            title: t("homepage.review_concept.bio_emb_supply_d1_title"),
            body: t("homepage.review_concept.bio_emb_supply_d1_body"),
          },
          {
            title: t("homepage.review_concept.bio_emb_supply_d2_title"),
            body: t("homepage.review_concept.bio_emb_supply_d2_body"),
          },
        ],
      },
    },
    {
      id: "embryo-normal-cycle",
      visual: "normal-cycle",
      label: t("homepage.review_concept.bio_emb_normal_label"),
      title: t("homepage.review_concept.bio_emb_normal_title"),
      body: t("homepage.review_concept.bio_emb_normal_body"),
      calloutLabel: t("homepage.review_concept.bio_callout_why_stim"),
      callout: t("homepage.review_concept.bio_emb_normal_callout"),
      detail: {
        title: t("homepage.review_concept.bio_emb_normal_detail_title"),
        sections: [
          {
            title: t("homepage.review_concept.bio_emb_normal_d1_title"),
            body: t("homepage.review_concept.bio_emb_normal_d1_body"),
          },
          {
            title: t("homepage.review_concept.bio_emb_normal_d2_title"),
            body: t("homepage.review_concept.bio_emb_normal_d2_body"),
          },
        ],
      },
    },
    {
      id: "embryo-follicles-eggs",
      visual: "follicle-cohort",
      label: t("homepage.review_concept.bio_emb_follicles_label"),
      title: t("homepage.review_concept.bio_emb_follicles_title"),
      body: t("homepage.review_concept.bio_emb_follicles_body"),
      calloutLabel: t("homepage.review_concept.bio_callout_key_distinction"),
      callout: t("homepage.review_concept.bio_emb_follicles_callout"),
      detail: {
        title: t("homepage.review_concept.bio_emb_follicles_detail_title"),
        sections: [
          {
            title: t("homepage.review_concept.bio_emb_follicles_d1_title"),
            body: t("homepage.review_concept.bio_emb_follicles_d1_body"),
          },
          {
            title: t("homepage.review_concept.bio_emb_follicles_d2_title"),
            body: t("homepage.review_concept.bio_emb_follicles_d2_body"),
          },
        ],
      },
    },
    {
      id: "embryo-stimulation",
      visual: "medication-signal",
      label: t("homepage.review_concept.bio_emb_stim_label"),
      title: t("homepage.review_concept.bio_emb_stim_title"),
      body: t("homepage.review_concept.bio_emb_stim_body"),
      calloutLabel: t("homepage.review_concept.bio_callout_reserve_note"),
      callout: t("homepage.review_concept.bio_emb_stim_callout"),
      detail: {
        title: t("homepage.review_concept.bio_emb_stim_detail_title"),
        sections: [
          {
            title: t("homepage.review_concept.bio_emb_stim_d1_title"),
            body: t("homepage.review_concept.bio_emb_stim_d1_body"),
          },
          {
            title: t("homepage.review_concept.bio_emb_stim_d2_title"),
            body: t("homepage.review_concept.bio_emb_stim_d2_body"),
          },
        ],
      },
    },
    {
      id: "embryo-monitoring-trigger-retrieval",
      visual: "monitoring",
      label: t("homepage.review_concept.bio_emb_monitor_label"),
      title: t("homepage.review_concept.bio_emb_monitor_title"),
      body: t("homepage.review_concept.bio_emb_monitor_body"),
      calloutLabel: t("homepage.review_concept.bio_callout_procedure_note"),
      callout: t("homepage.review_concept.bio_emb_monitor_callout"),
      detail: {
        title: t("homepage.review_concept.bio_emb_monitor_detail_title"),
        sections: [
          {
            title: t("homepage.review_concept.bio_emb_monitor_d1_title"),
            body: t("homepage.review_concept.bio_emb_monitor_d1_body"),
          },
          {
            title: t("homepage.review_concept.bio_emb_monitor_d2_title"),
            body: t("homepage.review_concept.bio_emb_monitor_d2_body"),
          },
          {
            title: t("homepage.review_concept.bio_emb_monitor_d3_title"),
            body: t("homepage.review_concept.bio_emb_monitor_d3_body"),
          },
        ],
      },
    },
    {
      id: "embryo-sperm-side",
      visual: "quality",
      label: t("homepage.review_concept.bio_emb_sperm_label"),
      title: t("homepage.review_concept.bio_emb_sperm_title"),
      body: t("homepage.review_concept.bio_emb_sperm_body"),
      calloutLabel: t("homepage.review_concept.bio_callout_what_to_know"),
      callout: t("homepage.review_concept.bio_emb_sperm_callout"),
      cta: {
        label: t("homepage.review_concept.bio_cta_check_semen"),
        screen: "AtHomeFertilityTesting",
      },
      detail: {
        title: t("homepage.review_concept.bio_emb_sperm_detail_title"),
        sections: [
          {
            title: t("homepage.review_concept.bio_emb_sperm_d1_title"),
            body: t("homepage.review_concept.bio_emb_sperm_d1_body"),
          },
          {
            title: t("homepage.review_concept.bio_emb_sperm_d2_title"),
            body: t("homepage.review_concept.bio_emb_sperm_d2_body"),
          },
        ],
      },
    },
    {
      id: "embryo-semen-sample",
      visual: "retrieval",
      label: t("homepage.review_concept.bio_emb_sample_label"),
      title: t("homepage.review_concept.bio_emb_sample_title"),
      body: t("homepage.review_concept.bio_emb_sample_body"),
      calloutLabel: t("homepage.review_concept.bio_callout_timing"),
      callout: t("homepage.review_concept.bio_emb_sample_callout"),
      detail: {
        title: t("homepage.review_concept.bio_emb_sample_detail_title"),
        sections: [
          {
            title: t("homepage.review_concept.bio_emb_sample_d1_title"),
            body: t("homepage.review_concept.bio_emb_sample_d1_body"),
          },
          {
            title: t("homepage.review_concept.bio_emb_sample_d2_title"),
            body: t("homepage.review_concept.bio_emb_sample_d2_body"),
          },
          {
            title: t("homepage.review_concept.bio_emb_sample_d3_title"),
            body: t("homepage.review_concept.bio_emb_sample_d3_body"),
          },
        ],
      },
    },
    {
      id: "embryo-sperm-quality",
      visual: "quality",
      label: t("homepage.review_concept.bio_emb_quality_label"),
      title: t("homepage.review_concept.bio_emb_quality_title"),
      body: t("homepage.review_concept.bio_emb_quality_body"),
      calloutLabel: t(
        "homepage.review_concept.bio_callout_optimization_window",
      ),
      callout: t("homepage.review_concept.bio_emb_quality_callout"),
      cta: {
        label: t("homepage.review_concept.bio_cta_check_semen"),
        screen: "AtHomeFertilityTesting",
      },
      detail: {
        title: t("homepage.review_concept.bio_emb_quality_detail_title"),
        sections: [
          {
            title: t("homepage.review_concept.bio_emb_quality_d1_title"),
            body: t("homepage.review_concept.bio_emb_quality_d1_body"),
          },
          {
            title: t("homepage.review_concept.bio_emb_quality_d2_title"),
            body: t("homepage.review_concept.bio_emb_quality_d2_body"),
          },
        ],
      },
    },
    {
      id: "embryo-ivf-icsi",
      visual: "maturity",
      label: t("homepage.review_concept.bio_emb_ivficsi_label"),
      title: t("homepage.review_concept.bio_emb_ivficsi_title"),
      body: t("homepage.review_concept.bio_emb_ivficsi_body"),
      calloutLabel: t("homepage.review_concept.bio_callout_what_decides"),
      callout: t("homepage.review_concept.bio_emb_ivficsi_callout"),
      detail: {
        title: t("homepage.review_concept.bio_emb_ivficsi_detail_title"),
        sections: [
          {
            title: t("homepage.review_concept.bio_emb_ivficsi_d1_title"),
            body: t("homepage.review_concept.bio_emb_ivficsi_d1_body"),
          },
          {
            title: t("homepage.review_concept.bio_emb_ivficsi_d2_title"),
            body: t("homepage.review_concept.bio_emb_ivficsi_d2_body"),
          },
          {
            title: t("homepage.review_concept.bio_emb_ivficsi_d3_title"),
            body: t("homepage.review_concept.bio_emb_ivficsi_d3_body"),
          },
        ],
      },
    },
    {
      id: "embryo-fertilization-check",
      visual: "maturity",
      label: t("homepage.review_concept.bio_emb_fertcheck_label"),
      title: t("homepage.review_concept.bio_emb_fertcheck_title"),
      body: t("homepage.review_concept.bio_emb_fertcheck_body"),
      calloutLabel: t("homepage.review_concept.bio_callout_key_distinction"),
      callout: t("homepage.review_concept.bio_emb_fertcheck_callout"),
      detail: {
        title: t("homepage.review_concept.bio_emb_fertcheck_detail_title"),
        sections: [
          {
            title: t("homepage.review_concept.bio_emb_fertcheck_d1_title"),
            body: t("homepage.review_concept.bio_emb_fertcheck_d1_body"),
          },
          {
            title: t("homepage.review_concept.bio_emb_fertcheck_d2_title"),
            body: t("homepage.review_concept.bio_emb_fertcheck_d2_body"),
          },
        ],
      },
    },
    {
      id: "embryo-culture",
      visual: "attrition",
      label: t("homepage.review_concept.bio_emb_culture_label"),
      title: t("homepage.review_concept.bio_emb_culture_title"),
      body: t("homepage.review_concept.bio_emb_culture_body"),
      calloutLabel: t("homepage.review_concept.bio_callout_why_updates_hard"),
      callout: t("homepage.review_concept.bio_emb_culture_callout"),
      detail: {
        title: t("homepage.review_concept.bio_emb_culture_detail_title"),
        sections: [
          {
            title: t("homepage.review_concept.bio_emb_culture_d1_title"),
            body: t("homepage.review_concept.bio_emb_culture_d1_body"),
          },
          {
            title: t("homepage.review_concept.bio_emb_culture_d2_title"),
            body: t("homepage.review_concept.bio_emb_culture_d2_body"),
          },
        ],
      },
    },
    {
      id: "embryo-blastocyst",
      visual: "attrition",
      label: t("homepage.review_concept.bio_emb_blast_label"),
      title: t("homepage.review_concept.bio_emb_blast_title"),
      body: t("homepage.review_concept.bio_emb_blast_body"),
      calloutLabel: t("homepage.review_concept.bio_callout_after_blastocyst"),
      callout: t("homepage.review_concept.bio_emb_blast_callout"),
      detail: {
        title: t("homepage.review_concept.bio_emb_blast_detail_title"),
        sections: [
          {
            title: t("homepage.review_concept.bio_emb_blast_d1_title"),
            body: t("homepage.review_concept.bio_emb_blast_d1_body"),
          },
          {
            title: t("homepage.review_concept.bio_emb_blast_d2_title"),
            body: t("homepage.review_concept.bio_emb_blast_d2_body"),
          },
        ],
      },
    },
    {
      id: "embryo-pgt-pgd",
      visual: "freezing",
      label: t("homepage.review_concept.bio_emb_pgt_label"),
      title: t("homepage.review_concept.bio_emb_pgt_title"),
      body: t("homepage.review_concept.bio_emb_pgt_body"),
      calloutLabel: t("homepage.review_concept.bio_callout_what_does"),
      callout: t("homepage.review_concept.bio_emb_pgt_callout"),
      detail: {
        title: t("homepage.review_concept.bio_emb_pgt_detail_title"),
        sections: [
          {
            title: t("homepage.review_concept.bio_emb_pgt_d1_title"),
            body: t("homepage.review_concept.bio_emb_pgt_d1_body"),
          },
          {
            title: t("homepage.review_concept.bio_emb_pgt_d2_title"),
            body: t("homepage.review_concept.bio_emb_pgt_d2_body"),
          },
          {
            title: t("homepage.review_concept.bio_emb_pgt_d3_title"),
            body: t("homepage.review_concept.bio_emb_pgt_d3_body"),
          },
        ],
      },
    },
    {
      id: "embryo-euploidy",
      visual: "quality",
      label: t("homepage.review_concept.bio_emb_euploidy_label"),
      title: t("homepage.review_concept.bio_emb_euploidy_title"),
      body: t("homepage.review_concept.bio_emb_euploidy_body"),
      calloutLabel: t("homepage.review_concept.bio_callout_important_limit"),
      callout: t("homepage.review_concept.bio_emb_euploidy_callout"),
      detail: {
        title: t("homepage.review_concept.bio_emb_euploidy_detail_title"),
        sections: [
          {
            title: t("homepage.review_concept.bio_emb_euploidy_d1_title"),
            body: t("homepage.review_concept.bio_emb_euploidy_d1_body"),
          },
          {
            title: t("homepage.review_concept.bio_emb_euploidy_d2_title"),
            body: t("homepage.review_concept.bio_emb_euploidy_d2_body"),
          },
        ],
      },
    },
    {
      id: "embryo-freezing-storage",
      visual: "freezing",
      label: t("homepage.review_concept.bio_emb_freeze_label"),
      title: t("homepage.review_concept.bio_emb_freeze_title"),
      body: t("homepage.review_concept.bio_emb_freeze_body"),
      calloutLabel: t("homepage.review_concept.bio_callout_how_long"),
      callout: t("homepage.review_concept.bio_emb_freeze_callout"),
      detail: {
        title: t("homepage.review_concept.bio_emb_freeze_detail_title"),
        sections: [
          {
            title: t("homepage.review_concept.bio_emb_freeze_d1_title"),
            body: t("homepage.review_concept.bio_emb_freeze_d1_body"),
          },
          {
            title: t("homepage.review_concept.bio_emb_freeze_d2_title"),
            body: t("homepage.review_concept.bio_emb_freeze_d2_body"),
          },
          {
            title: t("homepage.review_concept.bio_emb_freeze_d3_title"),
            body: t("homepage.review_concept.bio_emb_freeze_d3_body"),
          },
        ],
      },
    },
    {
      id: "embryo-future-transfer",
      visual: "retrieval",
      label: t("homepage.review_concept.bio_emb_future_label"),
      title: t("homepage.review_concept.bio_emb_future_title"),
      body: t("homepage.review_concept.bio_emb_future_body"),
      calloutLabel: t("homepage.review_concept.bio_callout_why_matters_freeze"),
      callout: t("homepage.review_concept.bio_emb_future_callout"),
      detail: {
        title: t("homepage.review_concept.bio_emb_future_detail_title"),
        sections: [
          {
            title: t("homepage.review_concept.bio_emb_future_d1_title"),
            body: t("homepage.review_concept.bio_emb_future_d1_body"),
          },
          {
            title: t("homepage.review_concept.bio_emb_future_d2_title"),
            body: t("homepage.review_concept.bio_emb_future_d2_body"),
          },
        ],
      },
    },
  ];
}

export type PreCycleBiologyVariant =
  "embryo-freezing" | "ivf" | "iui" | "amab-family-building";

/**
 * Shared source for the biology carousel variants used by both full
 * pre-cycle journeys and the Explorer options card. Keeping the copy
 * builders behind one exported entry point prevents those surfaces from
 * drifting apart as a journey is updated.
 */
export function buildPreCycleBiologySlides(
  variant: PreCycleBiologyVariant,
  t: TranslationFunction,
): DeepBiologySlide[] {
  switch (variant) {
    case "embryo-freezing":
      return buildEmbryoFreezingSlides(t);
    case "ivf":
      return buildIvfSlides(t);
    case "amab-family-building":
      return buildAmabSlides(t);
    case "iui":
      return buildIuiSlides(t);
  }
}

function buildIvfSlides(t: TranslationFunction): DeepBiologySlide[] {
  return buildEmbryoFreezingSlides(t).map((slide) => {
    if (slide.id === "embryo-egg-supply") {
      return {
        ...slide,
        id: "ivf-egg-supply",
        title: t("homepage.review_concept.bio_ivf_supply_title"),
        body: t("homepage.review_concept.bio_ivf_supply_body"),
        callout: t("homepage.review_concept.bio_ivf_supply_callout"),
        detail: {
          title: t("homepage.review_concept.bio_ivf_supply_detail_title"),
          sections: [
            {
              title: t("homepage.review_concept.bio_ivf_supply_d1_title"),
              body: t("homepage.review_concept.bio_ivf_supply_d1_body"),
            },
            {
              title: t("homepage.review_concept.bio_ivf_supply_d2_title"),
              body: t("homepage.review_concept.bio_ivf_supply_d2_body"),
            },
          ],
        },
      };
    }
    if (slide.id === "embryo-sperm-side") {
      return {
        ...slide,
        id: "ivf-sperm-side",
        title: t("homepage.review_concept.bio_ivf_sperm_title"),
        body: t("homepage.review_concept.bio_ivf_sperm_body"),
        detail: {
          title: t("homepage.review_concept.bio_ivf_sperm_detail_title"),
          sections: [
            {
              title: t("homepage.review_concept.bio_ivf_sperm_d1_title"),
              body: t("homepage.review_concept.bio_ivf_sperm_d1_body"),
            },
            {
              title: t("homepage.review_concept.bio_ivf_sperm_d2_title"),
              body: t("homepage.review_concept.bio_ivf_sperm_d2_body"),
            },
          ],
        },
      };
    }
    if (slide.id === "embryo-freezing-storage") {
      return {
        ...slide,
        id: "ivf-freezing-storage",
        title: t("homepage.review_concept.bio_ivf_freeze_title"),
        body: t("homepage.review_concept.bio_ivf_freeze_body"),
        callout: t("homepage.review_concept.bio_ivf_freeze_callout"),
        detail: {
          title: t("homepage.review_concept.bio_ivf_freeze_detail_title"),
          sections: [
            {
              title: t("homepage.review_concept.bio_ivf_freeze_d1_title"),
              body: t("homepage.review_concept.bio_ivf_freeze_d1_body"),
            },
            {
              title: t("homepage.review_concept.bio_ivf_freeze_d2_title"),
              body: t("homepage.review_concept.bio_ivf_freeze_d2_body"),
            },
          ],
        },
      };
    }
    if (slide.id === "embryo-future-transfer") {
      return {
        ...slide,
        id: "ivf-transfer",
        title: t("homepage.review_concept.bio_ivf_transfer_title"),
        body: t("homepage.review_concept.bio_ivf_transfer_body"),
        callout: t("homepage.review_concept.bio_ivf_transfer_callout"),
      };
    }
    return slide;
  });
}

function buildAmabSlides(t: TranslationFunction): DeepBiologySlide[] {
  const ivfSlides = buildIvfSlides(t);
  const reusedIds = new Set([
    "embryo-stimulation",
    "embryo-monitoring-trigger-retrieval",
    "embryo-ivf-icsi",
    "embryo-fertilization-check",
    "embryo-culture",
    "embryo-blastocyst",
    "embryo-pgt-pgd",
    "ivf-freezing-storage",
    "ivf-transfer",
  ]);
  return [
    {
      id: "amab-sperm-agency",
      visual: "quality",
      label: t("homepage.review_concept.bio_amab_agency_label"),
      title: t("homepage.review_concept.bio_amab_agency_title"),
      body: t("homepage.review_concept.bio_amab_agency_body"),
      calloutLabel: t("homepage.review_concept.bio_callout_why_start_here"),
      callout: t("homepage.review_concept.bio_amab_agency_callout"),
      cta: {
        label: t("homepage.review_concept.bio_cta_check_semen"),
        screen: "AtHomeFertilityTesting",
      },
      detail: {
        title: t("homepage.review_concept.bio_amab_agency_detail_title"),
        sections: [
          {
            title: t("homepage.review_concept.bio_amab_agency_d1_title"),
            body: t("homepage.review_concept.bio_amab_agency_d1_body"),
          },
          {
            title: t("homepage.review_concept.bio_amab_agency_d2_title"),
            body: t("homepage.review_concept.bio_amab_agency_d2_body"),
          },
        ],
      },
    },
    {
      id: "amab-source-plan",
      visual: "retrieval",
      label: t("homepage.review_concept.bio_amab_source_label"),
      title: t("homepage.review_concept.bio_amab_source_title"),
      body: t("homepage.review_concept.bio_amab_source_body"),
      calloutLabel: t("homepage.review_concept.bio_callout_timing"),
      callout: t("homepage.review_concept.bio_amab_source_callout"),
      detail: {
        title: t("homepage.review_concept.bio_amab_source_detail_title"),
        sections: [
          {
            title: t("homepage.review_concept.bio_amab_source_d1_title"),
            body: t("homepage.review_concept.bio_amab_source_d1_body"),
          },
          {
            title: t("homepage.review_concept.bio_amab_source_d2_title"),
            body: t("homepage.review_concept.bio_amab_source_d2_body"),
          },
        ],
      },
    },
    {
      id: "amab-egg-source",
      visual: "reserve",
      label: t("homepage.review_concept.bio_amab_eggsource_label"),
      title: t("homepage.review_concept.bio_amab_eggsource_title"),
      body: t("homepage.review_concept.bio_amab_eggsource_body"),
      calloutLabel: t("homepage.review_concept.bio_callout_different_timeline"),
      callout: t("homepage.review_concept.bio_amab_eggsource_callout"),
      detail: {
        title: t("homepage.review_concept.bio_amab_eggsource_detail_title"),
        sections: [
          {
            title: t("homepage.review_concept.bio_amab_eggsource_d1_title"),
            body: t("homepage.review_concept.bio_amab_eggsource_d1_body"),
          },
          {
            title: t("homepage.review_concept.bio_amab_eggsource_d2_title"),
            body: t("homepage.review_concept.bio_amab_eggsource_d2_body"),
          },
        ],
      },
    },
    ...ivfSlides.filter((slide) => reusedIds.has(slide.id)),
  ];
}

function buildIuiSlides(t: TranslationFunction): DeepBiologySlide[] {
  return [
    {
      id: "iui-fit",
      visual: "normal-cycle",
      label: t("homepage.review_concept.bio_iui_fit_label"),
      title: t("homepage.review_concept.bio_iui_fit_title"),
      body: t("homepage.review_concept.bio_iui_fit_body"),
      calloutLabel: t("homepage.review_concept.bio_callout_before_starting"),
      callout: t("homepage.review_concept.bio_iui_fit_callout"),
      detail: {
        title: t("homepage.review_concept.bio_iui_fit_detail_title"),
        sections: [
          {
            title: t("homepage.review_concept.bio_iui_fit_d1_title"),
            body: t("homepage.review_concept.bio_iui_fit_d1_body"),
          },
          {
            title: t("homepage.review_concept.bio_iui_fit_d2_title"),
            body: t("homepage.review_concept.bio_iui_fit_d2_body"),
          },
        ],
      },
    },
    {
      id: "iui-tube-status",
      visual: "monitoring",
      label: t("homepage.review_concept.bio_iui_tubes_label"),
      title: t("homepage.review_concept.bio_iui_tubes_title"),
      body: t("homepage.review_concept.bio_iui_tubes_body"),
      calloutLabel: t("homepage.review_concept.bio_callout_test_examples"),
      callout: t("homepage.review_concept.bio_iui_tubes_callout"),
      cta: {
        label: t("homepage.review_concept.bio_cta_find_tube"),
        screen: "FindCareScreen",
      },
      detail: {
        title: t("homepage.review_concept.bio_iui_tubes_detail_title"),
        sections: [
          {
            title: t("homepage.review_concept.bio_iui_tubes_d1_title"),
            body: t("homepage.review_concept.bio_iui_tubes_d1_body"),
          },
          {
            title: t("homepage.review_concept.bio_iui_tubes_d2_title"),
            body: t("homepage.review_concept.bio_iui_tubes_d2_body"),
          },
        ],
      },
    },
    {
      id: "iui-medication-choice",
      visual: "medication-signal",
      label: t("homepage.review_concept.bio_iui_med_label"),
      title: t("homepage.review_concept.bio_iui_med_title"),
      body: t("homepage.review_concept.bio_iui_med_body"),
      calloutLabel: t("homepage.review_concept.bio_callout_safety"),
      callout: t("homepage.review_concept.bio_iui_med_callout"),
      detail: {
        title: t("homepage.review_concept.bio_iui_med_detail_title"),
        sections: [
          {
            title: t("homepage.review_concept.bio_iui_med_d1_title"),
            body: t("homepage.review_concept.bio_iui_med_d1_body"),
          },
          {
            title: t("homepage.review_concept.bio_iui_med_d2_title"),
            body: t("homepage.review_concept.bio_iui_med_d2_body"),
          },
        ],
      },
    },
    {
      id: "iui-monitoring-trigger",
      visual: "trigger",
      label: t("homepage.review_concept.bio_iui_trigger_label"),
      title: t("homepage.review_concept.bio_iui_trigger_title"),
      body: t("homepage.review_concept.bio_iui_trigger_body"),
      calloutLabel: t("homepage.review_concept.bio_callout_common_timing"),
      callout: t("homepage.review_concept.bio_iui_trigger_callout"),
      detail: {
        title: t("homepage.review_concept.bio_iui_trigger_detail_title"),
        sections: [
          {
            title: t("homepage.review_concept.bio_iui_trigger_d1_title"),
            body: t("homepage.review_concept.bio_iui_trigger_d1_body"),
          },
          {
            title: t("homepage.review_concept.bio_iui_trigger_d2_title"),
            body: t("homepage.review_concept.bio_iui_trigger_d2_body"),
          },
        ],
      },
    },
    {
      id: "iui-sperm-prep",
      visual: "quality",
      label: t("homepage.review_concept.bio_iui_prep_label"),
      title: t("homepage.review_concept.bio_iui_prep_title"),
      body: t("homepage.review_concept.bio_iui_prep_body"),
      calloutLabel: t("homepage.review_concept.bio_callout_what_matters"),
      callout: t("homepage.review_concept.bio_iui_prep_callout"),
      cta: {
        label: t("homepage.review_concept.bio_cta_check_semen"),
        screen: "AtHomeFertilityTesting",
      },
      detail: {
        title: t("homepage.review_concept.bio_iui_prep_detail_title"),
        sections: [
          {
            title: t("homepage.review_concept.bio_iui_prep_d1_title"),
            body: t("homepage.review_concept.bio_iui_prep_d1_body"),
          },
          {
            title: t("homepage.review_concept.bio_iui_prep_d2_title"),
            body: t("homepage.review_concept.bio_iui_prep_d2_body"),
          },
        ],
      },
    },
    {
      id: "iui-procedure",
      visual: "retrieval",
      label: t("homepage.review_concept.bio_iui_proc_label"),
      title: t("homepage.review_concept.bio_iui_proc_title"),
      body: t("homepage.review_concept.bio_iui_proc_body"),
      calloutLabel: t("homepage.review_concept.bio_callout_afterward"),
      callout: t("homepage.review_concept.bio_iui_proc_callout"),
      detail: {
        title: t("homepage.review_concept.bio_iui_proc_detail_title"),
        sections: [
          {
            title: t("homepage.review_concept.bio_iui_proc_d1_title"),
            body: t("homepage.review_concept.bio_iui_proc_d1_body"),
          },
          {
            title: t("homepage.review_concept.bio_iui_proc_d2_title"),
            body: t("homepage.review_concept.bio_iui_proc_d2_body"),
          },
        ],
      },
    },
    {
      id: "iui-wait",
      visual: "attrition",
      label: t("homepage.review_concept.bio_iui_wait_label"),
      title: t("homepage.review_concept.bio_iui_wait_title"),
      body: t("homepage.review_concept.bio_iui_wait_body"),
      calloutLabel: t("homepage.review_concept.bio_callout_timing"),
      callout: t("homepage.review_concept.bio_iui_wait_callout"),
      detail: {
        title: t("homepage.review_concept.bio_iui_wait_detail_title"),
        sections: [
          {
            title: t("homepage.review_concept.bio_iui_wait_d1_title"),
            body: t("homepage.review_concept.bio_iui_wait_d1_body"),
          },
          {
            title: t("homepage.review_concept.bio_iui_wait_d2_title"),
            body: t("homepage.review_concept.bio_iui_wait_d2_body"),
          },
        ],
      },
    },
  ];
}

type EmbryoProcessStep = {
  id: string;
  label: string;
  timing: string;
  title: string;
  icon: "eggs" | "panel" | "genetics" | "sperm" | "process";
};

function buildEmbryoProcessSteps(t: TranslationFunction): EmbryoProcessStep[] {
  return [
    {
      id: "ovarian",
      label: t("homepage.review_concept.process_embryo_ovarian_label"),
      timing: t("homepage.review_concept.process_embryo_ovarian_timing"),
      title: t("homepage.review_concept.process_embryo_ovarian_title"),
      icon: "eggs",
    },
    {
      id: "sperm",
      label: t("homepage.review_concept.process_embryo_sperm_label"),
      timing: t("homepage.review_concept.process_embryo_sperm_timing"),
      title: t("homepage.review_concept.process_embryo_sperm_title"),
      icon: "panel",
    },
    {
      id: "lab",
      label: t("homepage.review_concept.process_embryo_lab_label"),
      timing: t("homepage.review_concept.process_embryo_lab_timing"),
      title: t("homepage.review_concept.process_embryo_lab_title"),
      icon: "genetics",
    },
  ];
}

function buildIvfProcessSteps(t: TranslationFunction): EmbryoProcessStep[] {
  return [
    {
      id: "ovarian",
      label: t("homepage.review_concept.process_ivf_ovarian_label"),
      timing: t("homepage.review_concept.process_ivf_ovarian_timing"),
      title: t("homepage.review_concept.process_ivf_ovarian_title"),
      icon: "eggs",
    },
    {
      id: "sperm",
      label: t("homepage.review_concept.process_ivf_sperm_label"),
      timing: t("homepage.review_concept.process_ivf_sperm_timing"),
      title: t("homepage.review_concept.process_ivf_sperm_title"),
      icon: "sperm",
    },
    {
      id: "lab-transfer",
      label: t("homepage.review_concept.process_ivf_lab_label"),
      timing: t("homepage.review_concept.process_ivf_lab_timing"),
      title: t("homepage.review_concept.process_ivf_lab_title"),
      icon: "genetics",
    },
  ];
}

function buildAmabProcessSteps(t: TranslationFunction): EmbryoProcessStep[] {
  return [
    {
      id: "sperm",
      label: t("homepage.review_concept.process_amab_sperm_label"),
      timing: t("homepage.review_concept.process_amab_sperm_timing"),
      title: t("homepage.review_concept.process_amab_sperm_title"),
      icon: "sperm",
    },
    {
      id: "egg-source",
      label: t("homepage.review_concept.process_amab_egg_label"),
      timing: t("homepage.review_concept.process_amab_egg_timing"),
      title: t("homepage.review_concept.process_amab_egg_title"),
      icon: "eggs",
    },
    {
      id: "carrier-lab",
      label: t("homepage.review_concept.process_amab_lab_label"),
      timing: t("homepage.review_concept.process_amab_lab_timing"),
      title: t("homepage.review_concept.process_amab_lab_title"),
      icon: "genetics",
    },
  ];
}

function buildIuiProcessSteps(t: TranslationFunction): EmbryoProcessStep[] {
  return [
    {
      id: "fit",
      label: t("homepage.review_concept.process_iui_fit_label"),
      timing: t("homepage.review_concept.process_iui_fit_timing"),
      title: t("homepage.review_concept.process_iui_fit_title"),
      icon: "process",
    },
    {
      id: "timing",
      label: t("homepage.review_concept.process_iui_timing_label"),
      timing: t("homepage.review_concept.process_iui_timing_timing"),
      title: t("homepage.review_concept.process_iui_timing_title"),
      icon: "eggs",
    },
    {
      id: "procedure",
      label: t("homepage.review_concept.process_iui_procedure_label"),
      timing: t("homepage.review_concept.process_iui_procedure_timing"),
      title: t("homepage.review_concept.process_iui_procedure_title"),
      icon: "sperm",
    },
  ];
}

function buildVariantConfig(
  variant: PreCycleBiologyVariant,
  t: TranslationFunction,
): {
  slides: DeepBiologySlide[];
  processSteps: EmbryoProcessStep[];
  attritionTitle?: string;
  attritionSubtitle?: string;
  showEmbryoFunnel?: boolean;
  comparisonAction?: {
    title: string;
    subtitle: string;
    icon: "genetics" | "process" | "sperm";
    action: PlanAction;
  };
  coverageVariant?: "embryo-freezing";
} {
  switch (variant) {
    case "embryo-freezing":
      return {
        slides: buildPreCycleBiologySlides(variant, t),
        processSteps: buildEmbryoProcessSteps(t),
        attritionTitle: t(
          "homepage.review_concept.understand_attrition_embryo_title",
        ),
        attritionSubtitle: t(
          "homepage.review_concept.understand_attrition_embryo_subtitle",
        ),
        showEmbryoFunnel: true,
        comparisonAction: {
          title: t("homepage.review_concept.understand_compare_embryo_title"),
          subtitle: t(
            "homepage.review_concept.understand_compare_embryo_subtitle",
          ),
          icon: "genetics",
          action: {
            label: t("homepage.review_concept.understand_compare_embryo_cta"),
            screen: "ArticleDetail",
            params: { articleId: "moon-article-eggs-vs-embryos" },
          },
        },
        coverageVariant: "embryo-freezing",
      };
    case "ivf":
      return {
        slides: buildPreCycleBiologySlides(variant, t),
        processSteps: buildIvfProcessSteps(t),
        attritionTitle: t(
          "homepage.review_concept.understand_attrition_ivf_title",
        ),
        attritionSubtitle: t(
          "homepage.review_concept.understand_attrition_ivf_subtitle",
        ),
        showEmbryoFunnel: true,
        comparisonAction: {
          title: t("homepage.review_concept.understand_compare_ivf_title"),
          subtitle: t(
            "homepage.review_concept.understand_compare_ivf_subtitle",
          ),
          icon: "genetics",
          action: {
            label: t("homepage.review_concept.understand_compare_ivf_cta"),
            screen: "LearnScreen",
            params: { topic: "ivf-lab-decisions" },
          },
        },
        coverageVariant: "embryo-freezing",
      };
    case "amab-family-building":
      return {
        slides: buildPreCycleBiologySlides(variant, t),
        processSteps: buildAmabProcessSteps(t),
        attritionTitle: t(
          "homepage.review_concept.understand_attrition_amab_title",
        ),
        attritionSubtitle: t(
          "homepage.review_concept.understand_attrition_amab_subtitle",
        ),
        showEmbryoFunnel: true,
        comparisonAction: {
          title: t("homepage.review_concept.understand_compare_amab_title"),
          subtitle: t(
            "homepage.review_concept.understand_compare_amab_subtitle",
          ),
          icon: "sperm",
          action: {
            label: t("homepage.review_concept.understand_compare_amab_cta"),
            screen: "LearnScreen",
            params: { topic: "amab-family-building" },
          },
        },
        coverageVariant: "embryo-freezing",
      };
    case "iui":
      return {
        slides: buildPreCycleBiologySlides(variant, t),
        processSteps: buildIuiProcessSteps(t),
        comparisonAction: {
          title: t("homepage.review_concept.understand_compare_iui_title"),
          subtitle: t(
            "homepage.review_concept.understand_compare_iui_subtitle",
          ),
          icon: "process",
          action: {
            label: t("homepage.review_concept.understand_compare_iui_cta"),
            screen: "LearnScreen",
            params: { topic: "iui-fit" },
          },
        },
      };
  }
}

function EmbryoProcessTimelineRow({ steps }: { steps: EmbryoProcessStep[] }) {
  return (
    <View style={{ flexDirection: "row", gap: 7 }}>
      {steps.map((step, index) => (
        <View
          key={step.id}
          style={{
            flex: 1,
            minWidth: 0,
            borderRadius: 15,
            backgroundColor: "rgba(255,255,255,0.46)",
            borderWidth: 1,
            borderColor: "rgba(92, 52, 32, 0.08)",
            paddingHorizontal: 9,
            paddingVertical: 10,
            gap: 7,
          }}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 5,
            }}
          >
            <OrganicMiniIcon kind={step.icon} size={30} />
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 10,
                lineHeight: 12,
                color: EMBER.colors.textFaint,
                fontWeight: "800",
              }}
            >
              0{index + 1}
            </Text>
          </View>
          <View style={{ gap: 3 }}>
            <Text
              className="font-dm-sans"
              numberOfLines={2}
              style={{
                fontSize: 10,
                lineHeight: 13,
                color: EMBER.colors.textLight,
                fontWeight: "800",
                textTransform: "uppercase",
              }}
            >
              {step.label}
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                lineHeight: 16,
                color: EMBER.colors.text,
                fontWeight: "800",
              }}
            >
              {step.timing}
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 11,
                lineHeight: 14,
                color: EMBER.colors.textMid,
                fontWeight: "500",
              }}
            >
              {step.title}
            </Text>
          </View>
        </View>
      ))}
    </View>
  );
}

export function UnderstandEmbryoFreezingStep({
  userAge,
  onAction,
  variant = "embryo-freezing",
  stepId = "precycle-embryo-freezing.understand",
  title,
  subtitle,
}: {
  userAge?: number;
  onAction: (action: PlanAction) => void;
  variant?: PreCycleBiologyVariant;
  stepId?: string;
  title?: string;
  subtitle?: string;
}) {
  const t = useTranslations();
  const config = buildVariantConfig(variant, t);
  const resolvedTitle =
    title ?? t("homepage.review_concept.understand_default_title");
  const resolvedSubtitle =
    subtitle ?? t("homepage.review_concept.understand_default_subtitle");

  return (
    <StepperSection
      id={stepId}
      stepLabel={t("homepage.review_concept.understand_step_label")}
      showRail={false}
    >
      <TimelineSubtaskCard
        id={`${stepId}.flow`}
        title={resolvedTitle}
        subtitle={resolvedSubtitle}
        titleIcon={<OrganicMiniIcon kind="process" size={34} />}
      >
        <View style={{ gap: 10 }}>
          <EmbryoProcessTimelineRow steps={config.processSteps} />
          <BiologyDeepCarousel
            userAge={userAge}
            onAction={onAction}
            slides={config.slides}
          />
        </View>
      </TimelineSubtaskCard>

      {config.showEmbryoFunnel ? (
        <TimelineSubtaskCard
          id={`${stepId}.attrition`}
          title={
            config.attritionTitle ??
            t("homepage.review_concept.understand_attrition_embryo_title")
          }
          subtitle={
            config.attritionSubtitle ??
            t("homepage.review_concept.understand_attrition_embryo_subtitle")
          }
          titleIcon={<OrganicMiniIcon kind="eggs" size={34} />}
          collapsedPreviewHeight={68}
        >
          <OutcomeEstimateFunnelSection
            userAge={userAge}
            onAction={onAction}
            variant="embryo-freezing"
          />
        </TimelineSubtaskCard>
      ) : null}

      {config.comparisonAction ? (
        <TimelineActionRow
          title={config.comparisonAction.title}
          subtitle={config.comparisonAction.subtitle}
          icon={
            <OrganicMiniIcon kind={config.comparisonAction.icon} size={34} />
          }
          onPress={() => onAction(config.comparisonAction!.action)}
        />
      ) : null}

      {config.coverageVariant ? (
        <CoverageActionRow
          onAction={onAction}
          variant={config.coverageVariant}
        />
      ) : null}
    </StepperSection>
  );
}
